/**
 * Package catalogue management.
 *
 * Replaces `renderPackagesList`, `fetchPackages`, `handleAddPackage`,
 * `deletePackage`, `openEditPackageModal`, `handleEditPackage`,
 * `openPackageOfferModal`, `handleAddPackageOffer`, `setPackageTab` and the
 * `convertDateToISO` / `convertDateToDisplay` pair (now in `domain/dates`).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ConfirmDialog,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  Panel,
  Select,
  Spinner,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { adminApi } from '../../api/endpoints';
import { validatePackage } from '../../domain/validation';
import { isOfferValid } from '../../domain/offers';
import { formatCurrency } from '../../domain/pricing';
import { isoToDisplayDate, todayKey } from '../../domain/dates';

const PACKAGE_TYPES = [
  { value: 'posts', label: 'بوستات', icon: 'image', unit: 'بوست' },
  { value: 'reels', label: 'ريلز', icon: 'film', unit: 'ريل' },
  { value: 'combo', label: 'بوستات + ريلز', icon: 'layers', unit: 'بوست' },
  { value: 'photography', label: 'تصوير', icon: 'camera', unit: 'ساعة' },
];

const typeMeta = (value) => PACKAGE_TYPES.find((t) => t.value === value) || PACKAGE_TYPES[0];

/** The legacy card's one-line summary: "12 بوست" or "8 بوست + 4 ريل". */
const packageSummary = (pkg) => {
  const meta = typeMeta(pkg.type);
  if (pkg.type === 'combo') return `${pkg.quantity} بوست + ${pkg.reelQuantity || 0} ريل`;
  return `${pkg.quantity} ${meta.unit}`;
};

/** The inline add row, which starts on whichever type tab is open. */
const emptyDraft = (type) => ({ name: '', quantity: '', reelQuantity: '', price: '', type });

const emptyPackage = () => ({
  name: '',
  type: 'posts',
  quantity: 12,
  reelQuantity: 0,
  price: 2500,
  offerName: '',
  offerPercent: '',
  offerExpiry: '',
});

export function AdminPackages() {
  const { success, error: toastError } = useNotifications();
  const state = useAsync(() => adminApi.packages(), [], { initialData: [] });
  const packages = useMemo(() => state.data || [], [state.data]);

  // The legacy screen had no "all" tab — you are always inside one type, which
  // is also what the inline add row needs to know.
  const [tab, setTab] = useState('posts');
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(() => emptyDraft('posts'));
  const [draftError, setDraftError] = useState('');

  const counts = useMemo(
    () =>
      Object.fromEntries(
        PACKAGE_TYPES.map((type) => [type.value, packages.filter((p) => p.type === type.value).length]),
      ),
    [packages],
  );

  const filtered = packages.filter((p) => p.type === tab);

  const selectTab = (value) => {
    setTab(value);
    setDraft(emptyDraft(value));
    setDraftError('');
  };

  /** The inline row: name, counts, price, add. */
  const addFromDraft = async (event) => {
    event.preventDefault();
    if (!draft.name.trim()) return setDraftError('اكتب اسم الباقة');
    if (!Number(draft.quantity)) return setDraftError('أدخل العدد');
    if (tab === 'combo' && !Number(draft.reelQuantity)) return setDraftError('أدخل عدد الريلز');
    if (!Number(draft.price)) return setDraftError('أدخل السعر');

    setDraftError('');
    await save({ ...draft, type: tab });
    setDraft(emptyDraft(tab));
  };

  const save = async (pkg) => {
    setBusy(true);
    try {
      const payload = {
        name: pkg.name.trim(),
        type: pkg.type,
        quantity: Number(pkg.quantity) || 1,
        reelQuantity: pkg.type === 'combo' ? Number(pkg.reelQuantity) || 0 : null,
        price: Number(pkg.price) || 0,
        offerName: pkg.offerPercent ? pkg.offerName || 'عرض خاص' : null,
        offerPercent: pkg.offerPercent ? Number(pkg.offerPercent) : null,
        offerExpiry: pkg.offerPercent ? pkg.offerExpiry || null : null,
      };

      if (pkg.id) {
        await adminApi.updatePackage(pkg.id, payload);
        success('تم تحديث الباقة');
      } else {
        await adminApi.createPackage(payload);
        success('تمت إضافة الباقة');
      }

      setEditing(null);
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر حفظ الباقة');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await adminApi.deletePackage(deleteTarget.id);
      success('تم حذف الباقة');
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر حذف الباقة');
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* The whole screen is one purple-spined panel, as the legacy had it —
          the type tabs, the add row and the grid belong together. */}
      <Panel className="border-r-4 border-r-purple-500">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-xl font-bold text-white">
            <Icon name="package" size={24} className="text-purple-400" />
            باقات الخدمات
          </h3>
          <span className="rounded bg-purple-500/20 px-2 py-1 text-xs text-purple-400">
            خدمات للأطباء
          </span>
        </div>

        {/* Type tabs — four equal buttons, filled when active */}
        <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {PACKAGE_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => selectTab(type.value)}
              aria-pressed={tab === type.value}
              className={`package-tab flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold ${
                tab === type.value ? 'active' : ''
              }`}
            >
              <Icon name={type.icon} size={16} />
              {type.label}
              {counts[type.value] > 0 && (
                <span
                  className={`rounded-full px-1.5 text-[10px] ${
                    tab === type.value ? 'bg-white/25' : 'bg-white/10'
                  }`}
                >
                  {counts[type.value]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Inline add row — the legacy let you add a package without a modal,
            which is much faster when entering several in a row. */}
        <form
          onSubmit={addFromDraft}
          className="mb-6 rounded-xl bg-white/5 p-4"
        >
          <div
            className={`grid gap-3 ${tab === 'combo' ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} sm:grid-cols-2`}
          >
            <Input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="اسم الباقة"
              aria-label="اسم الباقة"
            />
            <Input
              type="number"
              min="1"
              value={draft.quantity}
              onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))}
              placeholder={tab === 'photography' ? 'عدد الساعات' : 'عدد التصميمات'}
              aria-label="العدد"
            />
            {tab === 'combo' && (
              <Input
                type="number"
                min="1"
                value={draft.reelQuantity}
                onChange={(e) => setDraft((d) => ({ ...d, reelQuantity: e.target.value }))}
                placeholder="عدد الريلز"
                aria-label="عدد الريلز"
              />
            )}
            <Input
              type="number"
              min="0"
              value={draft.price}
              onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
              placeholder="السعر (ج.م)"
              aria-label="السعر"
            />
            <Button
              type="submit"
              icon="plus"
              loading={busy}
              className="!bg-purple-600 hover:!bg-purple-500 justify-center py-3"
            >
              إضافة باقة
            </Button>
          </div>

          {draftError && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-red-300">
              <Icon name="alert-circle" size={13} />
              {draftError}
            </p>
          )}
        </form>

        {/* Grid */}
        {state.error ? (
          <ErrorState error={state.error} onRetry={state.reload} />
        ) : state.loading && packages.length === 0 ? (
          <Spinner variant="inline" label="جاري تحميل الباقات..." />
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-slate-400">
            <Icon name="package-x" size={48} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">لا توجد باقات في هذا القسم</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                onEdit={() => setEditing({ ...emptyPackage(), ...pkg })}
                onDelete={() => setDeleteTarget(pkg)}
              />
            ))}
          </div>
        )}
      </Panel>

      <PackageModal pkg={editing} busy={busy} onClose={() => setEditing(null)} onSave={save} />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        loading={busy}
        destructive
        title="حذف الباقة"
        confirmLabel="حذف"
        message={`سيتم حذف "${deleteTarget?.name || ''}" من الكتالوج.\n\nالطلبات القائمة على هذه الباقة لن تتأثر.`}
      />
    </div>
  );
}

/* ========================================================================== */

/**
 * One package.
 *
 * The legacy card carried the offer as a floating ribbon in the corner rather
 * than a block inside the body — it is the thing a doctor is meant to notice
 * first, and burying it under the title defeated that. The price shows struck
 * through beside the discounted one, so the saving is visible without
 * arithmetic.
 */
function PackageCard({ pkg, onEdit, onDelete }) {
  const offer = pkg.offerPercent
    ? { percent: pkg.offerPercent, expiry: pkg.offerExpiry, name: pkg.offerName }
    : null;
  const offerLive = offer && isOfferValid(offer);
  const finalPrice = offerLive
    ? Math.round(pkg.price - pkg.price * (pkg.offerPercent / 100))
    : pkg.price;

  const meta = typeMeta(pkg.type);

  return (
    <div
      className={`glass-panel relative flex h-full flex-col rounded-2xl border p-5 transition-colors ${
        offerLive
          ? 'border-purple-500/20 hover:border-purple-500/40'
          : 'border-white/5 hover:border-white/10'
      }`}
    >
      {offerLive && (
        <span className="pkg-offer-badge absolute left-3 top-3 z-10 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 px-3 py-1.5 text-xs font-bold text-white shadow-lg">
          {offer.name ? `${offer.name} ${offer.percent}%` : `خصم ${offer.percent}%`}
        </span>
      )}

      <div className="mb-4 flex items-start gap-3">
        <span className="rounded-lg bg-purple-500/10 p-2 text-purple-400">
          <Icon name={meta.icon} size={20} />
        </span>
        <div className="min-w-0 max-w-[60%]">
          <h4 className="truncate font-bold text-white">{pkg.name}</h4>
          <p className="text-xs text-slate-400">{packageSummary(pkg)}</p>
        </div>
      </div>

      <div className="mb-4 flex-1">
        {offerLive ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-purple-400">
                {formatCurrency(finalPrice)}
              </span>
              <span className="text-sm text-slate-500 line-through">{pkg.price}</span>
            </div>
            {offer.expiry && (
              <p className="mt-2 flex items-center gap-1 text-xs text-orange-400">
                <Icon name="clock" size={12} />
                العرض ينتهي: {isoToDisplayDate(offer.expiry) || offer.expiry}
              </p>
            )}
          </>
        ) : (
          <>
            <span className="text-2xl font-bold text-purple-400">{formatCurrency(pkg.price)}</span>
            {offer && (
              <p className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                <Icon name="clock" size={12} />
                عرض منتهٍ ({offer.percent}%)
              </p>
            )}
          </>
        )}
      </div>

      <div className="mt-auto flex gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="flex-1 rounded-lg bg-cyan-500/10 py-2 text-xs font-bold text-cyan-400 transition-colors hover:bg-cyan-500/20"
        >
          تعديل
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="flex-1 rounded-lg bg-orange-500/10 py-2 text-xs font-bold text-orange-400 transition-colors hover:bg-orange-500/20"
        >
          {offer ? 'تعديل العرض' : 'إضافة عرض'}
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`حذف ${pkg.name}`}
          className="rounded-lg bg-red-500/10 px-3 py-2 text-red-400 transition-colors hover:bg-red-500/20"
        >
          <Icon name="trash-2" size={14} />
        </button>
      </div>
    </div>
  );
}

/* ========================================================================== */

function PackageModal({ pkg, busy, onClose, onSave }) {
  const [form, setForm] = useState(pkg);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(pkg);
    setErrors({});
  }, [pkg]);

  if (!form) return null;

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = () => {
    const validation = validatePackage(form);
    setErrors(validation.errors);
    if (!validation.valid) return;
    onSave(form);
  };

  const preview = form.offerPercent
    ? Math.round(Number(form.price) - Number(form.price) * (Number(form.offerPercent) / 100))
    : Number(form.price);

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? 'تعديل الباقة' : 'إضافة باقة'}
      icon="package"
      size="md"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-bold text-emerald-400">
            {formatCurrency(Number.isFinite(preview) ? preview : 0)}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              إلغاء
            </Button>
            <Button icon="save" loading={busy} onClick={handleSubmit}>
              حفظ
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="اسم الباقة" error={errors.name} required>
          <Input value={form.name} onChange={update('name')} placeholder="مثال: باقة السوشيال ميديا" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="النوع" error={errors.type} required>
            <Select value={form.type} onChange={update('type')}>
              {PACKAGE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="السعر" error={errors.price} required>
            <Input type="number" min="0" value={form.price} onChange={update('price')} />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="الكمية" error={errors.quantity} required>
            <Input type="number" min="1" value={form.quantity} onChange={update('quantity')} />
          </Field>

          {form.type === 'combo' && (
            <Field label="عدد الريلز" hint="بالإضافة إلى البوستات">
              <Input
                type="number"
                min="0"
                value={form.reelQuantity}
                onChange={update('reelQuantity')}
              />
            </Field>
          )}
        </div>

        <section className="space-y-4 border-t border-white/10 pt-4">
          <h4 className="text-sm font-bold text-cyan-300">عرض ترويجي (اختياري)</h4>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="اسم العرض">
              <Input value={form.offerName || ''} onChange={update('offerName')} placeholder="عرض خاص" />
            </Field>

            <Field label="نسبة الخصم (%)">
              <Input
                type="number"
                min="0"
                max="100"
                value={form.offerPercent || ''}
                onChange={update('offerPercent')}
              />
            </Field>
          </div>

          <Field label="تاريخ انتهاء العرض" hint="اتركه فارغاً لعرض بلا تاريخ انتهاء">
            <Input
              type="date"
              dir="ltr"
              min={todayKey()}
              value={form.offerExpiry || ''}
              onChange={update('offerExpiry')}
            />
          </Field>
        </section>
      </div>
    </Modal>
  );
}

export default AdminPackages;
